import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import type { PostDetailData } from "@/hooks/useSearch";
import { CopyButton } from "./CopyButton";
import { DownloadPanel } from "./DownloadPanel";
import { FavoriteButton } from "./FavoriteButton";
import { TelegramSendButton } from "./TelegramSendButton";
import { useCurrentUser } from "@/hooks/useAuth";
import {
  useTelegramLinkStatus,
  useTelegramStatus,
} from "@/hooks/useTelegram";

// Posts whose source format Telegram can handle natively without a
// conversion. Static images deliberately omitted — image posts have no
// download/transcode pipeline so there's nothing to forward to a channel.
const TELEGRAM_SOURCE_EXTS = new Set(["gif", "webm", "apng", "mp4"]);
const STATIC_IMAGE_EXTS = new Set(["jpg", "jpeg", "png", "webp"]);
import { PlaybackTimeline } from "./PlaybackTimeline";
import { usePreferences } from "@/store/preferences";

const CATEGORY_COLORS: Record<string, string> = {
  artist: "text-tag-artist",
  character: "text-tag-character",
  species: "text-tag-species",
  copyright: "text-tag-copyright",
  meta: "text-tag-meta",
  lore: "text-tag-lore",
  general: "text-tag-general",
};

interface TagGroupProps {
  label: string;
  tags: string[];
  onTagClick: (tag: string) => void;
}

function TagGroup({ label, tags, onTagClick }: TagGroupProps) {
  if (tags.length === 0) return null;
  const colorClass = CATEGORY_COLORS[label] ?? "text-zinc-300";
  return (
    <div className="mb-3">
      <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">{label}</p>
      <div className="flex flex-wrap gap-1">
        {tags.map((tag) => (
          <button
            key={tag}
            onClick={() => onTagClick(tag)}
            className={`text-xs px-2 py-0.5 rounded bg-surface-2 hover:bg-surface-3 transition-colors ${colorClass}`}
          >
            {tag.replace(/_/g, " ")}
          </button>
        ))}
      </div>
    </div>
  );
}

interface Props {
  post: PostDetailData;
}

export function PostDetail({ post }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const navigate = useNavigate();
  const { addToBlacklist, blacklist } = usePreferences();
  const { data: currentUser } = useCurrentUser();
  const { data: telegramStatus } = useTelegramStatus();
  const telegramEnabled =
    !!currentUser && !!telegramStatus?.configured;
  const { data: telegramLink } = useTelegramLinkStatus(telegramEnabled);
  const canSendSourceToTelegram =
    telegramEnabled && TELEGRAM_SOURCE_EXTS.has(post.file_ext);
  const canDmSource =
    canSendSourceToTelegram && !!telegramLink?.linked;

  const isVideo = post.file_ext === "webm";
  const isImage = STATIC_IMAGE_EXTS.has(post.file_ext);
  // Animations go through the proxy (cached on first view). Static images
  // stream directly from e621's CDN at sample resolution — we don't want
  // to spend our disk caching every still anyone happens to look at.
  // Falls back to the proxied file URL if sample_url is missing (shouldn't
  // happen post-ingest, but be defensive).
  const mediaUrl = isImage
    ? post.sample_url ?? `/api/v1/media/file/${post.id}`
    : `/api/v1/media/file/${post.id}`;

  function handleTagClick(tag: string) {
    navigate(`/search?q=${encodeURIComponent(tag)}`);
  }

  const tagGroups = [
    { label: "artist", tags: post.artist_tags },
    { label: "character", tags: post.character_tags },
    { label: "species", tags: post.species_tags },
    { label: "copyright", tags: post.copyright_tags },
    { label: "lore", tags: post.lore_tags },
    { label: "meta", tags: post.meta_tags },
    { label: "general", tags: post.general_tags },
  ];

  return (
    <div className="grid lg:grid-cols-[1fr_300px] gap-6">
      {/* Media */}
      <div>
        <div className="relative bg-surface-1 rounded-xl overflow-hidden flex items-center justify-center">
          {isVideo ? (
            <video
              ref={videoRef}
              src={mediaUrl}
              controls
              loop
              muted
              autoPlay
              className="max-w-full max-h-[70vh] object-contain"
            />
          ) : (
            <img
              src={mediaUrl}
              alt={`Post ${post.id}`}
              className="max-w-full max-h-[70vh] object-contain"
            />
          )}
          {/* webm gets the timeline via the native <video controls> bar.
              For gif/apng we draw our own approximate one — the browser exposes
              no playback API for animated images. Static images get nothing. */}
          {!isVideo && !isImage && (
            <PlaybackTimeline duration={post.duration} active className="h-1.5" />
          )}
        </div>

        {/* Meta row */}
        <div className="mt-3 flex flex-wrap gap-3 text-sm text-zinc-400">
          <span>ID: <span className="text-zinc-300 font-mono">{post.id}</span></span>
          <span>Score: <span className="text-zinc-300">{post.score}</span></span>
          <span>Favs: <span className="text-zinc-300">{post.fav_count}</span></span>
          <span>Rating: <span className="text-zinc-300 uppercase">{post.rating}</span></span>
          <span>{post.file_ext.toUpperCase()}</span>
          {post.width && post.height && (
            <span>{post.width}×{post.height}</span>
          )}
          {post.duration && (
            <span>{post.duration.toFixed(1)}s</span>
          )}
        </div>

        {/* Sources */}
        {post.sources.length > 0 && (
          <div className="mt-2">
            <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Sources</p>
            <ul className="space-y-0.5">
              {post.sources.slice(0, 5).map((src) => (
                <li key={src}>
                  <a
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-accent-light hover:underline truncate block max-w-full"
                  >
                    {src}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        {/* Favorite (signed-in users only — FavoriteButton returns null otherwise) */}
        <div className="bg-surface-1 rounded-xl p-4 flex items-center justify-between">
          <span className="text-sm text-zinc-400">Save for later</span>
          <FavoriteButton postId={post.id} />
        </div>

        {/* Links: outbound + shareable cached-file URL */}
        <div className="bg-surface-1 rounded-xl p-4">
          <h2 className="font-semibold text-sm mb-3">Links</h2>
          <div className="flex flex-col gap-2 text-sm">
            <a
              href={`https://e621.net/posts/${post.id}`}
              target="_blank"
              rel="noreferrer noopener"
              className="text-zinc-300 hover:text-accent-light transition-colors min-h-[40px] inline-flex items-center"
            >
              Open in e621 ↗
            </a>
            <CopyButton
              text={`${window.location.origin}/api/v1/media/file/${post.id}`}
              label="Copy file link"
              hint="Permanent link to our cached copy of this file — keeps working after e621 takedowns"
              className="text-sm text-zinc-300 hover:text-accent-light transition-colors min-h-[40px] inline-flex items-center justify-start"
            />
            {isImage && post.file_url && (
              <a
                href={post.file_url}
                target="_blank"
                rel="noreferrer noopener"
                className="text-zinc-300 hover:text-accent-light transition-colors min-h-[40px] inline-flex items-center"
              >
                View full resolution on e621 ↗
              </a>
            )}
            {canSendSourceToTelegram && (
              <TelegramSendButton
                postId={post.id}
                preset={null}
                channelUrl={telegramStatus?.channel_url ?? null}
                variant="full"
                target="channel"
              />
            )}
            {canDmSource && (
              <TelegramSendButton
                postId={post.id}
                preset={null}
                channelUrl={null}
                variant="full"
                target="dm"
              />
            )}
          </div>
        </div>

        {/* Download — animations only. Image posts have no transcode pipeline,
            and the backend 422s on a static-ext download request anyway. */}
        {!isImage && (
          <div className="bg-surface-1 rounded-xl p-4">
            <h2 className="font-semibold text-sm mb-3">Export</h2>
            <DownloadPanel postId={post.id} duration={post.duration} />
          </div>
        )}

        {/* Tags */}
        <div className="bg-surface-1 rounded-xl p-4">
          <h2 className="font-semibold text-sm mb-3">Tags</h2>
          {tagGroups.map(({ label, tags }) => (
            <TagGroup key={label} label={label} tags={tags} onTagClick={handleTagClick} />
          ))}
        </div>

        {/* Blacklist helpers */}
        <div className="bg-surface-1 rounded-xl p-4 text-sm">
          <h2 className="font-semibold mb-2">Blacklist a tag</h2>
          <div className="flex flex-wrap gap-1">
            {post.tags.slice(0, 20).filter((t) => !blacklist.includes(t)).map((tag) => (
              <button
                key={tag}
                onClick={() => addToBlacklist(tag)}
                className="text-xs px-2 py-0.5 rounded bg-surface-2 hover:bg-red-900/50 text-zinc-400 hover:text-red-300 transition-colors"
                title={`Blacklist ${tag}`}
              >
                − {tag.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
